/* eslint-disable react/jsx-indent */
import Clarity from '@microsoft/clarity'
import moment from 'moment-timezone'
import { isWaitlistBookingPayload } from '../../utils/appointmentSlotStatus'
import { store } from '../../store.index'
import { displayMoment } from '../../utils/displayTimezone'

/**
 * Get the time format setting from the store for a specific center
 * @param {string|number} centerId - Optional center ID to get settings from
 * @returns {string} - '12' or '24'
 */
export const getTimeFormatSetting = centerId => {
  try {
    const centersById = store.getState().centerreducer.centersById
    if (centerId && centersById?.[centerId]) {
      return centersById[centerId]?.settings?.calendar?.time_format || '12'
    }
    // Fallback to first center in current region
    const regionId = localStorage.getItem('region_id')
    const regionsById = store.getState().centerreducer.regionsById
    const region = regionsById?.[regionId]
    const settings = region?.centers?.[0]?.settings
    return settings?.calendar?.time_format || '12'
  } catch {
    return '12'
  }
}

const DEFAULT_DATE_FORMAT = 'jan_8_2026'

const SHORT_DATE_PATTERNS = {
  jan_8_2026: 'MMM D',
  '8_jan_2026': 'D MMM',
  '08_jan_2026': 'DD MMM'
}

/**
 * Get the center date format setting (`format.date_format`) from the store
 * @param {string|number} centerId - Optional center ID to get settings from
 * @returns {string} - 'jan_8_2026' | '8_jan_2026' | '08_jan_2026'
 */
export const getDateFormatSetting = centerId => {
  try {
    const { centersById, regionsById } = store.getState().centerreducer
    const settings =
      centerId && centersById?.[centerId]
        ? centersById[centerId]?.settings
        : regionsById?.[localStorage.getItem('region_id')]?.centers?.[0]
            ?.settings
    const value = settings?.format?.date_format
    return SHORT_DATE_PATTERNS[value] ? value : DEFAULT_DATE_FORMAT
  } catch {
    return DEFAULT_DATE_FORMAT
  }
}

/**
 * Month + day pattern (no year) respecting the center date format
 * @param {string|number} centerId - Optional center ID to get settings from
 * @param {boolean} withDay - Prefix the weekday, e.g. "Tue, Sep 12"
 * @returns {string} - moment.js format string
 */
export const getShortDatePattern = (centerId, withDay = false) => {
  const pattern = SHORT_DATE_PATTERNS[getDateFormatSetting(centerId)]
  return withDay ? `ddd, ${pattern}` : pattern
}

/**
 * Format a datetime as month + day respecting the center date format
 * @param {string|Date|moment.Moment} datetime - The datetime to format
 * @param {string|number} centerId - Optional center ID to get settings from
 * @param {boolean} withDay - Prefix the weekday
 * @returns {string} - e.g. "Sep 12" or "12 Sep"
 */
export const formatShortDate = (datetime, centerId, withDay = false) => {
  if (!datetime) return ''
  return displayMoment(datetime).format(getShortDatePattern(centerId, withDay))
}

/**
 * Get the moment.js time format string based on center settings
 * @param {boolean} uppercase - Whether to use uppercase AM/PM (default: false)
 * @param {string|number} centerId - Optional center ID to get settings from
 * @returns {string} - The moment.js format string ('h:mm a' for 12-hour, 'HH:mm' for 24-hour)
 */
export const getTimeFormat = (uppercase = false, centerId) => {
  const timeFormat = getTimeFormatSetting(centerId)
  if (timeFormat === '24') {
    return 'HH:mm'
  }
  return uppercase ? 'h:mm A' : 'h:mm a'
}

/**
 * Format a datetime to time string based on center settings
 * @param {string|Date|moment.Moment} datetime - The datetime to format
 * @param {boolean} uppercase - Whether to use uppercase AM/PM (default: false)
 * @param {string|number} centerId - Optional center ID to get settings from
 * @returns {string} - The formatted time string
 */
export const formatTime = (datetime, uppercase = false, centerId) => {
  if (!datetime) return ''
  return displayMoment(datetime).format(getTimeFormat(uppercase, centerId))
}

/** Studio wall clock for booking payloads. Display conversion must not change the booked slot. */
export const formatStudioTime = (datetime, uppercase = false, centerId) => {
  if (!datetime) return ''
  return moment(datetime).format(getTimeFormat(uppercase, centerId))
}

/**
 * Format a datetime with a custom format that includes time, respecting center settings
 * @param {string|Date|moment.Moment} datetime - The datetime to format
 * @param {string} formatString - The format string with time placeholder
 * @param {string|number} centerId - Optional center ID to get settings from
 * @returns {string} - The formatted datetime string
 */
export const formatDateTimeWithTime = (datetime, formatString, centerId) => {
  if (!datetime) return ''
  const timeFormat = getTimeFormatSetting(centerId)
  let adjustedFormat = formatString

  if (timeFormat === '24') {
    // Replace 12-hour patterns with 24-hour equivalents
    adjustedFormat = adjustedFormat
      .replace(/h:mm a/gi, 'HH:mm')
      .replace(/hh:mm a/gi, 'HH:mm')
      .replace(/h:mma/gi, 'HH:mm')
      .replace(/hh:mma/gi, 'HH:mm')
      .replace(/h:mm A/g, 'HH:mm')
      .replace(/hh:mm A/g, 'HH:mm')
      .replace(/hh:mm aa/gi, 'HH:mm')
  }

  return displayMoment(datetime).format(adjustedFormat)
}

/**
 * Convert time string to 12/24 hour format based on center settings
 * @param {string} time - Time string in HH:mm format
 * @param {string|number} centerId - Optional center ID to get settings from
 * @returns {string} - Formatted time string
 */
export function tConvert(time, centerId) {
  if (!time) return ''
  // Check if 24-hour format is enabled
  const timeFormat = getTimeFormatSetting(centerId)
  if (timeFormat === '24') {
    // Return time in 24-hour format (HH:mm)
    const match = time
      .toString()
      .match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/)
    if (match) {
      return `${match[1]}:${match[3]}`
    }
    return time
  }

  // Original 12-hour conversion logic
  // Check correct time format and split into components
  time = time.toString().match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [
    time
  ]

  if (time.length > 1) {
    // If time format correct
    time = time.slice(1) // Remove full string match value
    time[5] = +time[0] < 12 ? 'AM' : 'PM' // Set AM/PM
    time[0] = +time[0] % 12 || 12 // Adjust hours
  }
  return time.join('') // return adjusted time or original string
}

export const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname.includes('ngrok') ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.1/8 is considered localhost for IPv4.
    window.location.port != '' ||
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
)

export function getUrlParams(param) {
  let host = window.location.host.split('.')
  isLocalhost
    ? localStorage.setItem('url_prefix', 'f11boxing1')
    : localStorage.setItem('url_prefix', host[0])

  Clarity.setTag('url_prefix', localStorage.getItem('url_prefix'))
  return localStorage.getItem('url_prefix')
}

export function daysInThisMonth() {
  var now = new Date()
  return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()
}

export function replaceImage(e, image) {
  e.target.src = image
}

export function isJson(str) {
  try {
    JSON.parse(str)
  } catch (e) {
    return false
  }
  return true
}

/**
 * Maps legacy type values to new thank-you URL types
 * @param {string} type - The legacy type value (e.g., 'sale', 'book_purchase', 'quickpay')
 * @param {object|string} data - The data object or JSON string containing event details
 * @returns {string} - The new URL type (e.g., 'classes', 'appointments', 'memberships')
 */
export function getThankYouUrlType(type, data) {
  // Parse data if it's a string
  let parsedData = data
  if (typeof data === 'string') {
    try {
      parsedData = JSON.parse(decodeURIComponent(data))
    } catch {
      parsedData = {}
    }
  }

  const eventType = parsedData?.event_type
  const serviceId = parsedData?.service_id

  switch (type) {
    case 'book_purchase':
    case 'quickpay':
      // For bookings, determine class vs appointment based on event_type or ptData
      if (eventType === 'appointment' || parsedData?.ptData) {
        return 'appointments'
      }
      return 'classes'

    case 'sale':
    case 'course_purchase':
      // For sales, check service_id: 8 = gift card, 6 = course
      if (serviceId === 8) {
        return 'gift-card'
      }
      if (serviceId === 6) {
        return 'courses'
      }
      return 'memberships'

    case 'cartSale':
    case 'cart_purchase':
      return 'cart'

    case 'vod':
      return 'vod'

    case 'waitlist':
      return 'waitlist'

    case 'booking':
      // Simple booking without purchase - check event_type
      if (eventType === 'appointment' || parsedData?.ptData) {
        return 'appointments'
      }
      return 'classes'

    default:
      // For unknown types, try to infer from data
      if (eventType === 'appointment' || parsedData?.ptData) {
        return 'appointments'
      }
      if (eventType === 'class') {
        return 'classes'
      }
      // Default to memberships for sale-like types
      return 'memberships'
  }
}

/**
 * Resolves thank-you URL type for purchase-and-book flows, including waitlist joins.
 */
export function getBookPurchaseThankYouUrlType(
  type,
  data,
  ...bookingStatusSources
) {
  let parsedData = data
  if (typeof data === 'string') {
    try {
      parsedData = JSON.parse(decodeURIComponent(data))
    } catch {
      parsedData = {}
    }
  }

  if (
    parsedData?.is_waitlist_join &&
    (parsedData?.event_type === 'appointment' || parsedData?.ptData)
  ) {
    return 'waitlist'
  }

  if (bookingStatusSources.some(source => isWaitlistBookingPayload(source))) {
    if (parsedData?.event_type === 'appointment' || parsedData?.ptData) {
      return 'waitlist'
    }
  }

  return getThankYouUrlType(type, data)
}

export { default as Tooltip } from './Tooltip'
